import React, { useMemo, useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";
import { useToast } from "../../contexts/ToastContext";
import { useDevice } from "../../_layout";
import GlobalStyles from "../../../Resources/Styles";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { formatNumber, formatNumber2Dec } from "../../utils/numberUtils";
import Tooltip from "../../Components/Tooltip";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";

const ExerciseHistoryRow = ({ exercise, isLast, isFirst }) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const { showToast } = useToast();
  const [isHovered, setIsHovered] = useState(false);
  const [isCopyHovered, setIsCopyHovered] = useState(false);

  const coach = useMemo(() => {
    return (
      exercise?.workout?.block?.client?.coach ??
      exercise?.workout?.block?.coach ??
      exercise?.coach ??
      exercise?.workout?.coach ??
      null
    );
  }, [exercise]);

  const rirOrRpe = coach?.rirOrRpe ?? false;

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const locale = i18n?.language || "en";
    try {
      const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(
        d
      );
      return `${month} ${d.getDate()}, ${d.getFullYear()}`;
    } catch (e) {
      return d.toLocaleDateString();
    }
  };

  const getBackgroundColor = (serie) => {
    if (!serie) return Colors.blackColor;

    const target = serie.getTargetRpeReference;
    const actual = serie.rpe;

    if (typeof target === "number" && target > 0.02 && actual > 0.02) {
      const a = parseFloat(formatNumber2Dec(actual));
      const tVal = parseFloat(formatNumber2Dec(target));

      if (Math.abs(a - tVal) >= 1) {
        return a > tVal ? "rgba(240, 68, 56, 1)" : "rgba(93, 156, 96, 1)";
      }
    }

    return Colors.blackColor;
  };

  const handleCopySets = () => {
    if (!coach || !exercise) return;
    coach.copySetsFromExercise?.(exercise);
    const exerciseName = exercise.getName(t, i18n.language);
    showToast("setsCopied-string", {
      type: "info",
      title: "setsCopied-string",
      description: "setsCopiedDescription-string",
      variables: { exerciseName },
    });
  };

  const shouldShowCopy = isMobile || isHovered;
  const shouldDisableCopy = !coach || !exercise;
  const dateLabel = exercise?.workout?.date
    ? formatDate(exercise.workout.date)
    : "";
  const inlineSecondaryLabel =
    isMobile && dateLabel ? dateLabel : exercise.workout.name;
  const shouldShowDateRow = !isMobile && dateLabel;

  return (
    <View
      style={[
        styles.container,
        isLast && styles.containerLast,
        isFirst && styles.containerFirst,
      ]}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
    >
      <View style={styles.leftColumn}>
        <View style={styles.metaRow}>
          <Text numberOfLines={1} style={styles.primaryText}>
            {t("weekWithNumber-string", { week: exercise.workout.week })}
            <Text style={styles.secondaryText}> · {inlineSecondaryLabel}</Text>
          </Text>
        </View>
        {shouldShowDateRow && (
          <View style={styles.metaSubRow}>
            <Text numberOfLines={1} style={styles.dateText}>
              {dateLabel}
            </Text>
          </View>
        )}
        <View style={styles.metaSubRow}>
          <Text numberOfLines={1} style={styles.blockText}>
            {exercise.workout.block.name}
          </Text>
        </View>
      </View>

      <View style={styles.rightColumn}>
        {exercise.getSortedSeries.map((serie, index) => (
          <Text key={index} numberOfLines={1} style={styles.setText}>
            {formatNumber(serie.weight)} x {formatNumber(serie.reps)}{" "}
            <Text
              numberOfLines={1}
              style={[
                styles.setText,
                {
                  color:
                    getBackgroundColor(serie) === Colors.blackColor
                      ? Colors.darkgrayColor
                      : getBackgroundColor(serie),
                  fontWeight: "400",
                },
              ]}
            >
              {rirOrRpe ? "RIR" : "@"}
              {formatNumber(serie.rpe)}
            </Text>
          </Text>
        ))}
      </View>

      <View style={styles.copyButtonContainer}>
        <Tooltip content={t("copyTargetSets-string")} placement="top">
          <HoverableTouchableOpacity
            style={[
              styles.copyButton,
              { opacity: shouldShowCopy && !shouldDisableCopy ? 1 : 0 },
            ]}
            pointerEvents={
              shouldShowCopy && !shouldDisableCopy ? "auto" : "none"
            }
            onPress={handleCopySets}
            hoverStyle={undefined}
            onMouseEnter={() => !isMobile && setIsCopyHovered(true)}
            onMouseLeave={() => !isMobile && setIsCopyHovered(false)}
            disabled={shouldDisableCopy}
          >
            <Ionicons
              name="copy-outline"
              size={12}
              color={Colors.grayColor}
              style={{ opacity: isCopyHovered ? 0.7 : 1 }}
            />
          </HoverableTouchableOpacity>
        </Tooltip>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.whiteColor,
    backfaceVisibility: "hidden",
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 8,
    flexDirection: "row",
    gap: 8,
    position: "relative",
  },
  containerFirst: {
    paddingTop: 10,
  },
  containerLast: {
    borderBottomWidth: 0,
  },
  leftColumn: {
    gap: 2,
    flexShrink: 1,
    paddingRight: 24, // Space for copy button if needed, though it's on the right
  },
  rightColumn: {
    alignItems: "flex-end",
    gap: 2,
    paddingRight: 20, // Reserve space for the absolute copy button
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  primaryText: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  secondaryText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  metaSubRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  dateText: {
    fontSize: 11,
    color: Colors.grayColor,
    fontWeight: "400",
  },
  dotSeparator: {
    fontSize: 11,
    color: Colors.borderColor,
  },
  blockText: {
    fontSize: 11,
    color: Colors.grayColor,
    fontWeight: "400",
    flexShrink: 1,
  },
  setText: {
    color: Colors.darkgrayColor,
    fontSize: 12,
    marginVertical: 1,
    fontWeight: "400",
  },
  copyButtonContainer: {
    position: "absolute",
    top: 10,
    right: 4,
  },
  copyButton: {
    padding: 4,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default ExerciseHistoryRow;
